import React, { StrictMode, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { CommerceApiProvider } from '@salesforce/commerce-sdk-react';
import { withLegacyGetProps } from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-legacy-get-props';
import { withReactQuery } from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query';
import { useCorrelationId } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import PropTypes from 'prop-types';
import { CurrencyProvider } from '../../contexts/CurrencyContext';
import { MultiSiteProvider } from '../../contexts/MultiSiteContext';
import { theme } from '../../theme';
import { createUrlTemplate, resolveSiteFromUrl } from '../../utils/site-utils';
import { getEnv, getMobifyProxyPrefix, getOriginUrl } from '../../utils/url-utils';
import { resolveLocaleFromUrl } from '../../utils/utils';

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({ children, locals = {} }) => {
    const { correlationId } = useCorrelationId();
    const headers = {
        'correlation-id': correlationId,
    };

    const commerceApiConfig = locals.appConfig.commerceAPI;
    const { isSLASClientPrivate } = getEnv();
    const appOrigin = getOriginUrl();

    const prefix = useMemo(() => getMobifyProxyPrefix(), []);

    return (
        <StrictMode>
            <CommerceApiProvider
                shortCode={commerceApiConfig.parameters.shortCode}
                clientId={commerceApiConfig.parameters.clientId}
                organizationId={commerceApiConfig.parameters.organizationId}
                siteId={locals.site?.id}
                locale={locals.locale?.id}
                currency={locals.locale?.preferredCurrency}
                redirectURI={`${appOrigin}/callback`}
                proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
                headers={headers}
                OCAPISessionsURL={`${appOrigin}${prefix}/s/${locals.site?.id}/dw/shop/v24_1/sessions`}
                enablePWAKitPrivateClient={isSLASClientPrivate}
            >
                <MultiSiteProvider
                    site={locals.site}
                    locale={locals.locale}
                    buildUrl={locals.buildUrl}
                >
                    <CurrencyProvider currency={locals.locale?.preferredCurrency}>
                        <ChakraProvider theme={theme}>{children}</ChakraProvider>
                    </CurrencyProvider>
                </MultiSiteProvider>
                <ReactQueryDevtools />
            </CommerceApiProvider>
        </StrictMode>
    );
};

/**
 * This is called every time the application is loaded (one in server and one in client).
 * There is no access to the request here.
 * @param locals
 */
AppConfig.restore = (locals = {}) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`;
    const site = resolveSiteFromUrl(path);
    const locale = resolveLocaleFromUrl(path);

    const { app: appConfig } = getConfig();
    const apiConfig = {
        ...appConfig.commerceAPI,
    };

    apiConfig.parameters.siteId = site.id;

    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id);
    locals.site = site;
    locals.locale = locale;
    locals.appConfig = appConfig;
};

AppConfig.freeze = () => undefined;

AppConfig.extraGetPropsArgs = (locals = {}) => ({
    api: locals.api,
    buildUrl: locals.buildUrl,
    site: locals.site,
    locale: locals.locale,
});

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object,
};

const isServerSide = typeof window === 'undefined';

const options = {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false, // default: true
                staleTime: 10 * 1000,
                retryOnMount: !isServerSide,
            },
            mutations: {
                retry: false,
            },
        },
    },
};

export default withLegacyGetProps(withReactQuery(AppConfig, options));
